import React, { useState, useEffect } from "react";
import {
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Grid,
  Typography,
  Card,
  CardContent,
  useMediaQuery,
  Container,
  Paper,
  Button,
  Stack,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

// IMPORTANT: Import the ASYNC function
import allProducts from "../utils/allProducts.js";

import Copyright from "../components/CopyRights.js";
import ProductImageCarousel from "../components/ProductImageCarousel.jsx";
import HeyzineFlipbook from "../components/HeyzineFlipbook.js";
import aboutBG from "../assets/about_BG.png";

// --- Constants ---
const LOADING_DELAY_MS = 300;
const ITEMS_PER_PAGE = 20;

export default function Products() {
  // --- Data & Loading States ---
  const [products, setProducts] = useState([]);
  const [dataLoadingError, setDataLoadingError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // --- Filtering & Pagination States ---
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const isSmallScreen = useMediaQuery("(max-width:900px)");

  // --- Initial Data Fetch ---
  useEffect(() => {
    async function loadProducts() {
      try {
        const data = await allProducts();
        setProducts(data);
      } catch (error) {
        console.error("Error during initial product fetch:", error);
        setDataLoadingError("Failed to load products from the database.");
      } finally {
        setIsLoading(false);
      }
    }
    loadProducts();
  }, []);

  // --- Derived State ---
  const categories = products.length > 0 
    ? [...new Set(products.map((p) => p.category))] 
    : [];

  // Grouping criteria: separate "กระเป๋า..." from others
  const bagCategories = categories.filter((c) => c.startsWith("กระเป๋า"));
  const otherCategories = categories.filter((c) => !c.startsWith("กระเป๋า"));

  // Check state for the entire "กระเป๋า" master group
  const isAllBagsSelected =
    bagCategories.length > 0 &&
    bagCategories.every((c) => selectedCategories.includes(c));
  
  const isSomeBagsSelected =
    bagCategories.some((c) => selectedCategories.includes(c)) && !isAllBagsSelected;

  const filteredProducts = selectedCategories.length
    ? products.filter((p) => selectedCategories.includes(p.category))
    : products;

  const totalPages = Math.ceil(filteredProducts.length / ITEMS_PER_PAGE);
  const startIdx = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedProducts = filteredProducts.slice(startIdx, startIdx + ITEMS_PER_PAGE);

  // --- Handlers ---

  const updatePageWithLoading = (newPage) => {
    setIsLoading(true);
    setTimeout(() => {
      setCurrentPage(newPage);
      setIsLoading(false);
    }, LOADING_DELAY_MS);
  };

  const toggleCategory = (category) => {
    setIsLoading(true);
    setTimeout(() => {
      setSelectedCategories((prev) =>
        prev.includes(category)
          ? prev.filter((c) => c !== category)
          : [...prev, category]
      );
      setCurrentPage(1);
      setIsLoading(false);
    }, LOADING_DELAY_MS);
  };

  const goToPreviousPage = () => {
    const newPage = Math.max(currentPage - 1, 1);
    if (newPage !== currentPage) {
      updatePageWithLoading(newPage);
    }
  };

  const goToNextPage = () => {
    const newPage = Math.min(currentPage + 1, totalPages);
    if (newPage !== currentPage) {
      updatePageWithLoading(newPage);
    }
  };

  // Toggles the entire Bag group on or off
  const toggleBagGroup = (e) => {
    e.stopPropagation(); // Prevents accordion from opening/closing when clicking the checkbox
    setIsLoading(true);
    setTimeout(() => {
      if (isAllBagsSelected) {
        // Remove all bag categories
        setSelectedCategories((prev) => prev.filter((c) => !bagCategories.includes(c)));
      } else {
        // Add all bag categories while retaining existing non-bag selections
        setSelectedCategories((prev) => [
          ...prev.filter((c) => !bagCategories.includes(c)),
          ...bagCategories,
        ]);
      }
      setCurrentPage(1);
      setIsLoading(false);
    }, LOADING_DELAY_MS);
  };

  // --- Render Function ---
  const renderProductContent = () => {
    if (dataLoadingError) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" height={isSmallScreen ? 500 : 700}>
          <Typography variant="h6" color="error">{dataLoadingError}</Typography>
        </Box>
      );
    }

    if (isLoading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" height={isSmallScreen ? 500 : 700}>
          <CircularProgress size={50} />
        </Box>
      );
    }

    if (paginatedProducts.length === 0) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" height={isSmallScreen ? 500 : 700}>
          <Typography variant="h6" color="textSecondary">ไม่พบสินค้าในหมวดหมู่นี้.</Typography>
        </Box>
      );
    }

    return (
      <Grid container spacing={2}>
        {paginatedProducts.map((product) => (
          <Grid item xs={12} sm={4} md={3} key={product.id}>
            <Card variant="outlined" sx={{ height: "100%", backgroundColor: "#efe1c5" }}>
              <ProductImageCarousel product={product} />
              <CardContent
                sx={{
                  p: 0.75,
                  "&:last-child": { pb: 0.75 },
                }}
              >
                <Typography variant="body2" align="center">
                  <b>{product.id}</b> {product.name}
                  <b>
                    
                    {product.price === 101 ? (
                      <span style={{ color: "red" }}> @.-</span>
                    ) : (
                      <span style={{ color: "red" }}> @{product.price}.-</span>
                    )}
                  </b>
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <Grid
      container
      component="main"
      sx={{
        paddingTop: "55px",
        position: "relative",
        backgroundAttachment: "fixed",
        backgroundImage: `url(${aboutBG})`,
        backgroundRepeat: "repeat",
        backgroundSize: isSmallScreen ? "100%" : "33%",
        backgroundPosition: "center",
      }}
    >
      <Container>
        <Paper align="center" elevation={5} sx={{ marginTop: 4, padding: 2 }}>
          <Box display="flex" flexDirection={isSmallScreen ? "column" : "row"}>
            
            {/* Sidebar Filters */}
            <Box
              align="left"
              width={isSmallScreen ? "100%" : 250}
              p={2}
              borderRight={isSmallScreen ? 0 : "1px solid #ccc"}
              borderBottom={isSmallScreen ? "1px solid #ccc" : 0}
            >
              <Typography variant="h5" gutterBottom sx={{ fontWeight: "bold", mb: 2 }}>
                ประเภทสินค้า
              </Typography>

              {/* GROUP 1: กระเป๋า (Dropdown / Accordion) */}
              {bagCategories.length > 0 && (
                <Accordion disableGutters elevation={0} sx={{ "&:before": { display: "none" }, bgcolor: "transparent" }}>
                  <AccordionSummary 
                    expandIcon={<ExpandMoreIcon />} 
                    sx={{ p: 0, minHeight: 0, "& .MuiAccordionSummary-content": { margin: 0, alignItems: "center" } }}
                  >
                    <FormControlLabel
                      label={<Typography sx={{ fontWeight: "medium" }}>กระเป๋า</Typography>}
                      control={
                        <Checkbox
                          checked={isAllBagsSelected}
                          indeterminate={isSomeBagsSelected}
                          onChange={toggleBagGroup}
                          disabled={isLoading}
                        />
                      }
                    />
                  </AccordionSummary>
                  <AccordionDetails sx={{ pl: 1, pt: 0, pb: 1 }}>
                    <FormGroup>
                      {bagCategories.map((category) => (
                        <FormControlLabel
                          key={category}
                          control={
                            <Checkbox
                              size="small"
                              checked={selectedCategories.includes(category)}
                              onChange={() => toggleCategory(category)}
                              disabled={isLoading}
                            />
                          }
                          label={<Typography variant="body2">{category}</Typography>}
                        />
                      ))}
                    </FormGroup>
                  </AccordionDetails>
                </Accordion>
              )}

              {/* Structural Divider */}
              {bagCategories.length > 0 && otherCategories.length > 0 && (
                <Divider />
              )}

              {/* GROUP 2: Remaining Categories */}
              <FormGroup>
                {otherCategories.map((category) => (
                  // We use a React Fragment (<>...</>) to group the label and divider together per item
                  <React.Fragment key={category}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedCategories.includes(category)}
                          onChange={() => toggleCategory(category)}
                          disabled={isLoading}
                        />
                      }
                      label={category}
                    />
                    {/* This divider repeats for EVERY category in the loop */}
                    <Divider/> 
                  </React.Fragment>
                ))}
              </FormGroup>
            </Box>

            {/* Product Grid */}
            <Box flex={1} p={2}>
              {renderProductContent()}

              {/* Pagination Controls */}
              {filteredProducts.length > ITEMS_PER_PAGE && (
                <Stack direction="row" justifyContent="center" spacing={2} mt={4}>
                  <Button
                    variant="outlined"
                    onClick={goToPreviousPage}
                    disabled={currentPage === 1 || isLoading}
                  >
                    Previous
                  </Button>
                  <Typography variant="body1" sx={{ display: "flex", alignItems: "center" }} mt={1}>
                    Page {currentPage} of {totalPages}
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={goToNextPage}
                    disabled={currentPage === totalPages || isLoading}
                  >
                    Next
                  </Button>
                </Stack>
              )}
            </Box>
          </Box>
        </Paper>
        <Copyright />
      </Container>
    </Grid>
  );
}